import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { ReviewsTabProps } from './props'
import { Heading, Text } from '../../../../Atoms'
import { Flex, Grid, Pagination, Review } from '../../../../Molecules'
import { Rating } from './styles'
import { ReviewsContainer } from '../../../Reviews/styles'

const ReviewsTab = ({
    noReviewsText = 'No reviews',
    numberOfReviews = 0,
    perPage = 3,
    rating = 0,
    ratingText = 'Reviews from Trustpilot',
    reviews = [],
}: ReviewsTabProps): ReactElement => {
    const [showReviews, setShowReviews] = useState(reviews.slice(0, perPage))

    useEffect(() => {
        setShowReviews(reviews.slice(0, perPage))
    }, [perPage, reviews])

    const onPageChange = useCallback(
        (selectedPage: number) => {
            setShowReviews(
                reviews.slice(
                    (selectedPage - 1) * perPage,
                    selectedPage * perPage
                )
            )
        },
        [perPage, reviews]
    )

    const pageCount = Math.ceil(reviews.length / perPage)

    const shownReviews = useMemo(
        () =>
            showReviews.map((review, key) => (
                <Review
                    comment={review.comment}
                    date={review.date}
                    key={key}
                    name={review.name}
                    rating={review.rating}
                />
            )),
        [showReviews]
    )

    const ratingContainer = useMemo(
        () =>
            numberOfReviews > 0 && (
                <Rating>
                    <Grid>
                        <Text bold>{ratingText}</Text>
                        <Flex gap="4" alignItems="flex-end">
                            <Heading
                                level={4}
                                size={2}
                                primary
                                css={{ lineHeight: 1 }}
                            >
                                {rating.toFixed(2)}
                            </Heading>
                            <Text primary>SCORE</Text>
                        </Flex>
                    </Grid>
                    {perPage < reviews.length && (
                        <Pagination
                            onPageChange={onPageChange}
                            pageCount={pageCount}
                        />
                    )}
                </Rating>
            ),
        [
            numberOfReviews,
            onPageChange,
            pageCount,
            perPage,
            rating,
            ratingText,
            reviews.length,
        ]
    )

    const reviewsContainer = useMemo(
        () =>
            numberOfReviews > 0 ? (
                <ReviewsContainer
                    css={{
                        '@desktop': {
                            gridTemplateColumns: `repeat(${perPage}, 1fr)`,
                        },
                    }}
                >
                    {shownReviews}
                </ReviewsContainer>
            ) : (
                <Text>{noReviewsText}</Text>
            ),
        [noReviewsText, numberOfReviews, perPage, shownReviews]
    )

    return (
        <Grid gap="16">
            {ratingContainer}
            {reviewsContainer}
        </Grid>
    )
}

export default ReviewsTab
